import React, { useState, useEffect, useCallback, useMemo } from "react";

import contentTypes from "./contentTypes";
import layoutTypes from "./layoutTypes";
import { HubLayoutColumn } from "src/types/hub";

import { EdithubContext } from "../index";

import userRoleService from "src/utils/userRoles.service";

import _ from "lodash";
import cx from "classnames";
import styles from "./layout.module.scss";
import { LayoutPalette } from "src/types/adoption/hub";

export const defaultRow = [
  {
    width: 2,
    section: {
      id: "",
      title: "Section 1",
      entityType: "stream",
      layoutStyle: "carousel",
      cardStyle: "large",
      contents: [],
      smartFilter: "recently_viewed_streams",
      type: "carousel_portrait",
      rowIndex: 0,
    },
  },
];

interface ILayoutProps {
  setLayoutPalette: (palette: LayoutPalette) => void;
}

const Layout: React.FC<ILayoutProps> = ({setLayoutPalette}) => {

  const { updateRowItem, persistedRowItem, createSection } = React.useContext(EdithubContext);

  const [selectedEntityType, setSelectedEntityType] = useState<string>("stream");
  const [activeEntityRow, setActiveEntityRow] = useState<HubLayoutColumn[]>(defaultRow);
  const [activeLayout, setActiveLayout] = useState<string>("");
  const [activeLayoutIcon, setActiveLayoutIcon] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [layoutCount, setLayoutCount] = useState<number>(0);

  useEffect(() => {
    if (_.size(persistedRowItem) > 0) {
      setActiveEntityRow(persistedRowItem);
    }
  }, [persistedRowItem])


  useEffect(() => {
    setLayoutPalette(getLayoutPalette());
  },[activeLayout])

  useEffect(() => {
    if (!createSection && _.size(persistedRowItem) > 0) {
      setSelectedEntityType(persistedRowItem[0]?.section?.entityType);
    }
    else {
      setSelectedEntityType("stream");
    }
  }, [persistedRowItem])

  useEffect(() => {
    const hubActiveLayout = getActiveLayout();
    setActiveLayout(hubActiveLayout || "");
    setActiveLayoutIcon(`/images/hub/layouts/${hubActiveLayout}.gif`);

  }, [activeEntityRow]);

  const getLayoutPalette = () => {
    const layout = layoutTypes.layouts.find((e) => {
      return e.data.columns[0].section.type === activeLayout;
    })
    return _.get(layout,"name", "") as LayoutPalette;
  }
  
  const handleLayoutTypeChange = (layoutType) => {
    setActiveEntityRow(layoutType.data.columns);
    setActiveLayout(layoutType.data.columns[0].section.type);
    setActiveLayoutIcon(layoutType.icon);
    updateRowItem("rowItem", layoutType.data.columns);
  }

  const getLayoutType = useCallback(
    (entityType: string, cardStyle?: string, layoutStyle?: string) =>
      layoutTypes.layouts.find((e) => {
        const section = e.data.columns[0].section;

        return (
          section.entityType === entityType &&
          (!cardStyle || section.cardStyle === cardStyle) &&
          (!layoutStyle || section.layoutStyle === layoutStyle)
        );
      }),
    [],
  );

  const getActiveLayout = useCallback(() => {
    const section = activeEntityRow[0].section;
    const activeLayout = layoutTypes.layouts.find(
      (e) =>
        e.data.columns[0].section.entityType === section.entityType &&
        e.data.columns[0].section.layoutStyle === section.layoutStyle &&
        e.data.columns[0].section.cardStyle === section.cardStyle,
    );
    return activeLayout?.data.columns[0].section.type;
  }, [activeEntityRow]);

  const canAccessContentType = useCallback((entityType) => {
    return entityType.type === "collection"
      ? userRoleService.canAccessCollection()
      : true;
  }, []);

  const handleContentTypeClick = (entityType: string) => {
    const selectedLayout = getLayoutType(entityType);

    if (selectedLayout) {
      setActiveEntityRow(selectedLayout.data.columns);
      setActiveLayout(selectedLayout.data.columns[0].section.type);
      setActiveLayoutIcon(selectedLayout.icon);
      setSelectedEntityType(entityType)
      updateRowItem("rowItem", selectedLayout.data.columns);
    }
  };

  const renderLayoutTypes = useMemo(() => {
    const { entityType } = activeEntityRow[0].section;

    const filteredLayouts = layoutTypes.layouts.filter(
      (layoutType) =>
        layoutType.data.columns[0].section.entityType === entityType,
    );

    setLayoutCount(filteredLayouts.length);
    const activeIndex = _.findIndex(filteredLayouts, (layout) => {
      return _.get(layout, "data.columns[0].section.type") === activeLayout;
    });
    setActiveIndex(activeIndex);

    return filteredLayouts.map((layoutType, index) => {
      const sectionType = String(layoutType.data.columns[0].section.type);
      const isActive = sectionType === activeLayout;

      return (
        <div
          key={layoutType.id}
          className={cx(isActive ? styles.activeLayout : styles.layout)}
          onClick={() => {
            setActiveIndex(index);
            setActiveLayout(sectionType);
            handleLayoutTypeChange(layoutType);
            setActiveLayoutIcon(layoutType.icon);

          }}
        >
          <p className={styles.layoutName}>{layoutType.name}</p>
        </div>
      );
    });
  }, [activeEntityRow, activeLayout]);

  const description = useMemo(() => {
    const layout = layoutTypes.layouts.find(
      (e) => _.get(e,"data.columns[0].section.type") === activeLayout,
    );
    return layout?.description || "";
  }, [activeLayout]);

  return ( 
    <div className={styles.editSectionContent}>
      <div className={styles.contentTypeCards}>

        {/*  entity cards */}
        {contentTypes.contentTypes.map((entityType, index) => {
          if (canAccessContentType(entityType)) {
            const isActive = entityType.type === selectedEntityType;
            return (
              <div
                key={index}
                onClick={() => handleContentTypeClick(entityType.type)}
                className={cx(styles.contentTypeCard, {
                  [styles.activeContentCard]: isActive,
                })}
              >
                <div className={styles.contentTypeName}>
                  <i
                    className={cx(
                      isActive
                        ? "fa-solid fa-check-circle"
                        : "paperflite-radio-button-unselected",
                    )}
                    aria-hidden="true"
                  ></i>
                  <label>{entityType.name}</label>
                </div>
                <p className={styles.contentTypeDescription}>
                  {entityType.description}
                </p>
              </div>
            );
          }
          return null;
        })
        }
      </div>
      <div className={styles.layoutTypes}>
        <div className={styles.layouts}>
          <p className={styles.layoutHeading}>Layout Palette</p>
          <p className={styles.layoutDescription}>
            With a great range of layouts comes great responsibility! Don&apos;t
            forget to go through the description of each layout so you can
            choose the most suitable one.
          </p>
        </div>
        <div className={cx({
          [styles.showOverlay] : _.includes(activeLayoutIcon, "carousel") || _.includes(activeLayoutIcon, "promoted_content") || _.includes(activeLayoutIcon, "collections_folders")
        })}>
          <img src={activeLayoutIcon} className={styles.layoutIcon} />
        </div>
        <div className={styles.layoutNames}>{renderLayoutTypes}</div>
        <p className={styles.description}>{description}</p>
        <div className={styles.progressBarContainer}>
          <div className={styles.progressBars}>
            {Array.from({ length: layoutCount }).map((_, index) => (
              <div
                key={index}
                className={cx(styles.progressBar, {
                  [styles.activeProgressBar]: index === activeIndex,
                })}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
